/**
 * How yanamas helps me — the calibration profile page.
 *
 * These facets change HOW yanamas helps (style, pacing, depth) — never WHAT
 * yanamas is honest about. The engine owns the closed facet vocabulary and
 * validation; this page renders the 10 user-facing facets as selects plus the
 * behavioral-learning kill switch, and offers a full reset to defaults.
 *
 * "Default" is a display state, not an API value: when no row exists the
 * select shows "(default)" (a disabled option — un-setting a single facet
 * isn't supported; "Reset all" is the way back to defaults).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Card, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Check, RotateCcw, SlidersHorizontal, Sparkles } from 'lucide-react'
import { hortonGuidanceService } from '../services/hortonGuidanceService'
import { getReminderSettings, setReminderDefault } from '../services/reminderService'
import { BRAND } from '../config/brand'

interface FacetDef {
  facet: string
  label: string
  description: string
  options: Array<{ value: string; label: string }>
}

/** The 10 user-facing facets (behavioral_learning is rendered separately as
 *  a toggle). Values mirror the engine's closed vocabulary exactly. */
const FACETS: FacetDef[] = [
  {
    facet: 'question_tolerance',
    label: 'Questions',
    description: `How much should ${BRAND.assistant} ask before recommending?`,
    options: [
      { value: 'very_low', label: 'Barely any — just recommend' },
      { value: 'low', label: 'A question or two' },
      { value: 'normal', label: 'Ask what it takes to get it right' },
    ],
  },
  {
    facet: 'recommendation_style',
    label: 'Recommendations',
    description: `How should ${BRAND.assistant} put a suggestion forward?`,
    options: [
      { value: 'direct_tentative', label: 'One direct suggestion, held lightly' },
      { value: 'comparative', label: 'Lay out options and compare' },
    ],
  },
  {
    facet: 'plan_depth',
    label: 'Plan depth',
    description: 'How much of a plan at once?',
    options: [
      { value: 'one_start', label: 'Just one place to start' },
      { value: 'short_map', label: 'A short map of steps' },
      { value: 'detailed', label: 'A detailed plan' },
    ],
  },
  {
    facet: 'mapping_preference',
    label: 'Shaping ideas',
    description: 'Rough working version early, or shape it fully first?',
    options: [
      { value: 'working_version_early', label: 'Rough working version early' },
      { value: 'shape_first', label: 'Shape it fully before starting' },
    ],
  },
  {
    facet: 'step_size',
    label: 'Step size',
    description: 'How big should each next step feel?',
    options: [
      { value: 'tiny', label: 'Tiny — easy wins' },
      { value: 'moderate', label: 'Moderate' },
      { value: 'larger', label: 'Larger — bigger strides' },
    ],
  },
  {
    facet: 'challenge_candor',
    label: 'Pushback',
    description: `When ${BRAND.assistant} sees a problem with a plan, how direct should it be?`,
    options: [
      { value: 'gentle', label: 'Gentle — raise it softly' },
      { value: 'candid', label: 'Candid — say it straight' },
    ],
  },
  {
    facet: 'implied_item_acceptance',
    label: 'Adding items',
    description: `When a conversation implies a task or goal, what should ${BRAND.assistant} do?`,
    options: [
      { value: 'accepts', label: 'Add what we discussed' },
      { value: 'prefers_approval', label: 'Always ask before adding' },
      { value: 'frequently_dismisses', label: 'Suggest sparingly' },
    ],
  },
  {
    facet: 'alternatives_preference',
    label: 'Options',
    description: 'One best suggestion, or compare a few?',
    options: [
      { value: 'one_best', label: 'One best suggestion' },
      { value: 'compare', label: 'Compare a few' },
    ],
  },
  {
    facet: 'reversibility_preference',
    label: 'Commitment style',
    description: 'Try small experiments first, or commit directly?',
    options: [
      { value: 'experiments_first', label: 'Small experiments first' },
      { value: 'direct_commitment', label: 'Commit directly' },
    ],
  },
  {
    facet: 'detail_level',
    label: 'Detail',
    description: `How much detail in ${BRAND.assistant}’s replies?`,
    options: [
      { value: 'minimal', label: 'Minimal — just the essentials' },
      { value: 'moderate', label: 'Moderate' },
      { value: 'rich', label: 'Rich — context and reasoning' },
    ],
  },
]

export function HowHortonHelps() {
  // facet -> current value ('' = no row: yanamas' default applies).
  const [values, setValues] = useState<Record<string, string>>({})
  // facet -> 'explicit' | 'learned' (only for facets with a row).
  const [sources, setSources] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [engineDown, setEngineDown] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Which facet just saved (small inline "Saved" indicator, auto-clearing).
  const [savedFacet, setSavedFacet] = useState<string | null>(null)
  const savedTimer = useRef<number | null>(null)
  const [resetting, setResetting] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const profile = await hortonGuidanceService.getProfile()
      const nextValues: Record<string, string> = {}
      const nextSources: Record<string, string> = {}
      for (const f of profile.facets) {
        nextValues[f.facet] = f.value
        nextSources[f.facet] = f.source
      }
      setValues(nextValues)
      setSources(nextSources)
      setEngineDown(profile.engineUnavailable === true)
      setError(null)
    } catch {
      setEngineDown(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
    return () => {
      if (savedTimer.current) window.clearTimeout(savedTimer.current)
    }
  }, [load])

  const flashSaved = (facet: string) => {
    setSavedFacet(facet)
    if (savedTimer.current) window.clearTimeout(savedTimer.current)
    savedTimer.current = window.setTimeout(() => setSavedFacet(null), 2500)
  }

  const setFacet = async (facet: string, value: string) => {
    if (!value) return // "(default)" is display-only — no unset path in the API
    const previous = values[facet] ?? ''
    setValues((prev) => ({ ...prev, [facet]: value }))
    setSources((prev) => ({ ...prev, [facet]: 'explicit' }))
    setError(null)
    try {
      await hortonGuidanceService.setProfileFacet(facet, value)
      flashSaved(facet)
    } catch (e) {
      // Roll back so the UI never shows a value the engine rejected.
      setValues((prev) => ({ ...prev, [facet]: previous }))
      setError((e as Error).message || 'Could not save that preference.')
    }
  }

  const handleReset = async () => {
    if (!window.confirm(`Reset all of these to ${BRAND.assistant}’s defaults? Your learned and chosen preferences will be cleared.`)) return
    setResetting(true)
    setError(null)
    try {
      await hortonGuidanceService.resetProfile()
      await load()
    } catch (e) {
      setError((e as Error).message || 'Reset failed — please try again.')
    } finally {
      setResetting(false)
    }
  }

  const learningOn = (values['behavioral_learning'] ?? 'on') !== 'off'

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <SlidersHorizontal className="h-6 w-6" /> How {BRAND.assistant} helps me
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          These change HOW {BRAND.assistant} helps — style, pacing, depth. They never change what {BRAND.assistant} is
          honest about.
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-bad/10 px-3 py-2 text-sm text-bad">{error}</div>
      )}

      <ReminderPersistenceCard />

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading your preferences…</p>
      ) : engineDown ? (
        <Card>
          <CardContent className="pt-4 text-sm text-muted-foreground">
            {BRAND.assistant}' guidance engine isn't reachable right now. Your conversations still work —
            these preferences will be editable once it's back.
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="divide-y pt-2">
              {FACETS.map((def) => {
                const value = values[def.facet] ?? ''
                return (
                  <div
                    key={def.facet}
                    className="flex flex-col gap-2 py-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0 sm:pr-6">
                      <div className="flex items-center gap-2 font-medium">
                        {def.label}
                        {sources[def.facet] === 'learned' && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-brand-subtle px-2 py-0.5 text-xs text-brand">
                            <Sparkles className="h-3 w-3" /> learned
                          </span>
                        )}
                        {savedFacet === def.facet && (
                          <span className="inline-flex items-center gap-1 text-xs text-ok">
                            <Check className="h-3 w-3" /> Saved
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{def.description}</p>
                    </div>
                    <select
                      value={value}
                      onChange={(e) => void setFacet(def.facet, e.target.value)}
                      className="w-full shrink-0 rounded-lg border px-3 py-2 text-sm sm:w-64"
                      aria-label={def.label}
                    >
                      {/* Display-only: shown when no row exists; a set facet
                          can't return to default except via "Reset all". */}
                      <option value="" disabled>
                        (default)
                      </option>
                      {def.options.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )
              })}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center justify-between gap-4 pt-4">
              <div>
                <div className="font-medium">Let {BRAND.assistant} learn from my actions</div>
                <p className="text-sm text-muted-foreground">
                  When on, {BRAND.assistant} quietly adjusts these from what you actually do — dismissals,
                  quick saves, "map it now" clicks. Your explicit choices always win.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={learningOn}
                aria-label={`Let ${BRAND.assistant} learn from my actions`}
                onClick={() => void setFacet('behavioral_learning', learningOn ? 'off' : 'on')}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                  learningOn ? 'bg-brand' : 'bg-muted'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    learningOn ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button variant="outline" size="sm" disabled={resetting} onClick={() => void handleReset()}>
              <RotateCcw className="mr-1 h-4 w-4" /> {resetting ? 'Resetting…' : 'Reset all to defaults'}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}


/** Global reminder persistence default (F5 §10). Aggressive as the GLOBAL
 *  default asks the one-time factual acknowledgement — tiny, no moral
 *  warning, never re-asked. Per-reminder choices need no friction. */
function ReminderPersistenceCard() {
  const [level, setLevel] = useState<string>('persistent')
  const [ackCopy, setAckCopy] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    void getReminderSettings().then((s) => {
      if (s) setLevel(s.default_persistence)
    })
  }, [])

  const choose = async (lvl: string, acknowledge = false) => {
    setSaving(true)
    try {
      const r = await setReminderDefault(lvl, acknowledge)
      if (r?.needs_acknowledgement) {
        setAckCopy(r.copy || 'Aggressive reminders can repeatedly interrupt you until you resolve or change them.')
        return
      }
      if (r?.success) {
        setLevel(lvl)
        setAckCopy(null)
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardContent className="pt-4">
        <h3 className="text-sm font-bold text-primary">Reminder persistence</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          How new reminders behave by default — each reminder can override this.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {([
            { v: 'normal', label: 'Normal', hint: 'notify once' },
            { v: 'persistent', label: 'Persistent', hint: 'remind until resolved' },
            { v: 'aggressive', label: 'Aggressive', hint: 'frequent + escalating' },
          ]).map((o) => (
            <button
              key={o.v}
              type="button"
              disabled={saving}
              onClick={() => void choose(o.v)}
              className={`min-h-touch rounded-xl border px-4 text-left transition-colors ${
                level === o.v
                  ? 'border-brand bg-brand-subtle text-brand'
                  : 'border-soft bg-surface text-secondary hover:border-brand'
              }`}
            >
              <span className="block text-sm font-bold">{o.label}</span>
              <span className="block text-app-micro text-muted">{o.hint}</span>
            </button>
          ))}
        </div>
        {ackCopy && (
          <div className="mt-3 rounded-xl border border-warn/40 bg-warn/10 p-3">
            <p className="text-xs text-secondary">{ackCopy}</p>
            <button
              type="button"
              disabled={saving}
              onClick={() => void choose('aggressive', true)}
              className="mt-2 min-h-10 rounded-lg bg-warn px-4 text-xs font-bold text-white hover:opacity-90"
            >
              Use Aggressive
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
